/**
 * Расчёт «ледяного взрыва» (ТЗ §113): сетка льда, трещины, кривые времени.
 * Без DOM и без часов — только числа, поэтому всё проверяется юнит-тестом.
 *
 * Порт `frost-overlay.js` из хендоффа Claude Design (числа финальные, §113):
 * лёд — это «фронт» `front` в пикселях от края; точка покрыта, если её
 * эффективная дистанция до края `eff` меньше фронта. Анимация двигает только
 * `front`.
 */

/** Ячейка маски матового льда, px: маска считается в низком разрешении и растягивается. */
export const FROST_MASK_CELL = 8;
/** Seed трещин: тот же размер и seed дают ту же картину после любого ресайза. */
export const FROST_SEED = 7;
/** Глубина льда, % меньшей стороны (§113 п. 2). */
export const FROST_COVERAGE_PCT = 16;
/** Положение фронта «льда нет», px. */
export const FROST_BASE_FRONT = -90;
/** Нарастание льда при заморозке, с (§113 п. 1). */
export const FROST_BURST_S = 0.6;
/** Оттаивание, с (§113 п. 3). */
export const FROST_THAW_S = 1.5;
/** Вспышка: рост до полной яркости и угасание, с (§113 п. 1). */
export const FROST_FLASH_RISE_S = 0.06;
export const FROST_FLASH_FALL_S = 0.55;
/** Тряска: длительность, с, и амплитуда, px (§113 п. 1). */
export const FROST_SHAKE_S = 0.4;
export const FROST_SHAKE_AMPLITUDE = 7;
/** Всё, что длится после нажатия: дольше вспышки и тряски кадры не нужны. */
export const FROST_BURST_EFFECTS_S = FROST_FLASH_RISE_S + FROST_FLASH_FALL_S;

/** Круг оттаивания под курсором (ТЗ §118): лёд тает за 0,2 с, возвращается за 0,6 с. */
export const FROST_HOLE_IN_S = 0.2;
export const FROST_HOLE_OUT_S = 0.6;
/** Растушёвка края круга, доля радиуса. */
export const FROST_HOLE_FEATHER = 0.3;
/** Шаг, в пределах которого живой круг следует за курсором, доля радиуса. */
export const FROST_HOLE_STEP = 0.25;
/** Не больше стольких кругов сразу. */
export const FROST_HOLE_MAX = 16;

/** Перелёт easeOutBack при заморозке. */
const OVERSHOOT = 2.2;
/** Показатель кривой оттаивания. */
const THAW_EXPONENT = 2.6;
/** Ширина мягкого края льда, px. */
const SOFT_EDGE = 70;
/** Расстояние от края, на котором лёд становится максимально плотным, px. */
const DENSE_AT = 260;
/** Размах шума границы, px. */
const NOISE_SPAN = 150;

/** Один отрезок трещины; `birth` — значение `front`, с которого отрезок виден. */
export interface Crack {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  birth: number;
  width: number;
}

export interface FrostField {
  width: number;
  height: number;
  /** Размер сетки маски в ячейках. */
  cols: number;
  rows: number;
  /** Эффективная дистанция до края по центрам ячеек, построчно. */
  eff: Float32Array;
  /** Отрезки трещин, отсортированные по `birth`. */
  cracks: Crack[];
}

/** mulberry32: детерминированный генератор для трещин. */
function createRandom(seed: number): () => number {
  let state = seed | 0;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hash(i: number, j: number): number {
  let h = (Math.imul(i, 374761393) + Math.imul(j, 668265263)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}

function valueNoise(x: number, y: number): number {
  const i = Math.floor(x);
  const j = Math.floor(y);
  const fx = x - i;
  const fy = y - j;
  const u = fx * fx * (3 - 2 * fx);
  const v = fy * fy * (3 - 2 * fy);
  const a = hash(i, j);
  const b = hash(i + 1, j);
  const c = hash(i, j + 1);
  const d = hash(i + 1, j + 1);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}

/** Три октавы value noise, веса 0,55 / 0,30 / 0,15, масштабы 18 / 7 / 3. */
function fbm(x: number, y: number): number {
  return (
    valueNoise(x / 18, y / 18) * 0.55 +
    valueNoise(x / 7, y / 7) * 0.3 +
    valueNoise(x / 3, y / 3) * 0.15
  );
}

/** Эффективная дистанция до ближайшего края с шумом: граница льда выходит органичной. */
function effectiveDistance(width: number, height: number, x: number, y: number): number {
  const edge = Math.min(x, y, width - x, height - y);
  return edge - (fbm(x / FROST_MASK_CELL, y / FROST_MASK_CELL) - 0.5) * NOISE_SPAN;
}

/** Есть ли у экрана площадь: при нулевой стороне сетку и маску не строят. */
export function hasFrostArea(width: number, height: number): boolean {
  return width > 0 && height > 0;
}

/** Пересчёт при ресайзе: тот же размер и seed — та же картина. */
export function buildFrostField(width: number, height: number, seed = FROST_SEED): FrostField {
  if (!hasFrostArea(width, height)) {
    return { width: 0, height: 0, cols: 0, rows: 0, eff: new Float32Array(0), cracks: [] };
  }
  const cols = Math.ceil(width / FROST_MASK_CELL);
  const rows = Math.ceil(height / FROST_MASK_CELL);
  const rand = createRandom(seed);

  const eff = new Float32Array(cols * rows);
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      eff[j * cols + i] = effectiveDistance(
        width,
        height,
        (i + 0.5) * FROST_MASK_CELL,
        (j + 0.5) * FROST_MASK_CELL,
      );
    }
  }

  const cracks: Crack[] = [];
  const grow = (
    startX: number,
    startY: number,
    startAngle: number,
    length: number,
    depth: number,
    startBirth: number,
    baseWidth: number,
  ): void => {
    let x = startX;
    let y = startY;
    let angle = startAngle;
    let birth = startBirth;
    let done = 0;
    const head = startAngle;
    while (done < length) {
      const step = 18 + rand() * 22;
      if (rand() < 0.3) {
        angle += (rand() < 0.5 ? -1 : 1) * (0.4 + rand() * 0.5);
      }
      angle += (head - angle) * 0.08;
      const dir = angle + (rand() - 0.5) * 0.24;
      const nx = x + Math.cos(dir) * step;
      const ny = y + Math.sin(dir) * step;
      if (nx < 0 || ny < 0 || nx > width || ny > height) {
        return;
      }
      const t0 = done / length;
      done += step;
      const t1 = Math.min(1, done / length);
      // Монотонно: трещина растёт от основания к острию.
      birth = Math.max(birth, effectiveDistance(width, height, nx, ny)) + step * 0.5;
      cracks.push({
        x1: x,
        y1: y,
        x2: nx,
        y2: ny,
        birth,
        width: baseWidth * (1 - (t0 + t1) / 2) ** 0.9 + 0.35,
      });
      x = nx;
      y = ny;
      if (depth < 2 && t1 < 0.7 && rand() < (depth > 0 ? 0.08 : 0.18)) {
        const side = rand() < 0.5 ? -1 : 1;
        grow(
          x,
          y,
          dir + side * (0.6 + rand() * 0.7),
          (length - done) * (0.3 + rand() * 0.25),
          depth + 1,
          birth,
          baseWidth * (1 - t1) ** 0.9 * 0.7,
        );
      }
    }
  };

  const perimeter = 2 * (width + height);
  const perimeterStep = 95;
  for (let d = 0; d < perimeter; d += perimeterStep) {
    const q = d + rand() * perimeterStep;
    let x: number;
    let y: number;
    let angle: number;
    if (q < width) {
      x = q;
      y = 0;
      angle = Math.PI / 2;
    } else if (q < width + height) {
      x = width;
      y = q - width;
      angle = Math.PI;
    } else if (q < 2 * width + height) {
      x = 2 * width + height - q;
      y = height;
      angle = -Math.PI / 2;
    } else {
      x = 0;
      y = perimeter - q;
      angle = 0;
    }
    grow(x, y, angle + (rand() - 0.5) * 2.0, 60 + rand() * 110, 0, -999, 3.5 + rand() * 2.5);
  }
  cracks.sort((a, b) => a.birth - b.birth);

  return { width, height, cols, rows, eff, cracks };
}

/** Заморозка: easeOutBack с перелётом и откатом. */
export function easeFreeze(p: number): number {
  return 1 + (OVERSHOOT + 1) * (p - 1) ** 3 + OVERSHOOT * (p - 1) ** 2;
}

/** Оттаивание: плавное замедление. */
export function easeThaw(p: number): number {
  return 1 - (1 - p) ** THAW_EXPONENT;
}

/**
 * Прогресс оттаивания, при котором фронт тот же, что у взрыва на прогрессе `p`:
 * отжатие посреди взрыва не должно прыгать (перелёт выше глубины срезается).
 */
export function thawProgressFrom(freezeProgress: number): number {
  const eased = Math.min(1, easeFreeze(freezeProgress));
  if (eased <= 0) {
    return 0;
  }
  return 1 - (1 - eased) ** (1 / THAW_EXPONENT);
}

/** Глубина льда, px. */
export function frostCoverage(width: number, height: number): number {
  return (FROST_COVERAGE_PCT / 100) * Math.min(width, height);
}

/** Положение фронта по прогрессу `p`; `freezing` выбирает кривую. */
export function frostFront(p: number, freezing: boolean, coverage: number): number {
  const eased = freezing ? easeFreeze(p) : easeThaw(p);
  return FROST_BASE_FRONT + (coverage - FROST_BASE_FRONT) * eased;
}

/** Непрозрачность матового льда в точке на `g` px глубже фронта. */
export function maskAlpha(g: number): number {
  if (g <= 0) {
    return 0;
  }
  const edge = g >= SOFT_EDGE ? 1 : g / SOFT_EDGE;
  return edge * (0.72 + 0.28 * Math.min(1, Math.max(0, g / DENSE_AT)));
}

/** Пишет маску льда в RGBA-буфер: цвет белый, форму задаёт канал альфа. */
export function fillFrostMask(data: Uint8ClampedArray, eff: Float32Array, front: number): void {
  for (let k = 0; k < eff.length; k++) {
    const alpha = maskAlpha(front - (eff[k] ?? 0));
    data[k * 4] = 255;
    data[k * 4 + 1] = 255;
    data[k * 4 + 2] = 255;
    data[k * 4 + 3] = alpha * 255;
  }
}

/** Отрезки, уже родившиеся при данном фронте (входной список отсортирован по `birth`). */
export function visibleCracks(cracks: readonly Crack[], front: number): Crack[] {
  const visible: Crack[] = [];
  for (const crack of cracks) {
    if (crack.birth > front) {
      break;
    }
    visible.push(crack);
  }
  return visible;
}

/** Отрезки по ширине с шагом 0,5 px: один путь на группу вместо сотен штрихов. */
export function groupByWidth(cracks: readonly Crack[]): Map<number, Crack[]> {
  const groups = new Map<number, Crack[]>();
  for (const crack of cracks) {
    const key = Math.round(crack.width * 2) / 2;
    const list = groups.get(key);
    if (list === undefined) {
      groups.set(key, [crack]);
    } else {
      list.push(crack);
    }
  }
  return groups;
}

/** Ключ перерисовки: маска и трещины пересобираются, только когда фронт сдвинулся на 0,25 px. */
export function frontKey(front: number): number {
  return Math.round(front * 4);
}

/** Яркость вспышки через `elapsedS` секунд после нажатия. */
export function flashOpacity(elapsedS: number): number {
  if (elapsedS < FROST_FLASH_RISE_S) {
    return elapsedS / FROST_FLASH_RISE_S;
  }
  return Math.max(0, 1 - (elapsedS - FROST_FLASH_RISE_S) / FROST_FLASH_FALL_S);
}

/** Сдвиг тряски в px; `null` — тряска закончилась (контейнер возвращается на место). */
export function shakeOffset(elapsedS: number): { x: number; y: number } | null {
  const k = Math.max(0, 1 - elapsedS / FROST_SHAKE_S);
  const amplitude = FROST_SHAKE_AMPLITUDE * k * k;
  if (amplitude <= 0.05) {
    return null;
  }
  return { x: Math.sin(elapsedS * 90) * amplitude, y: Math.cos(elapsedS * 73) * amplitude };
}

/** Шаг прогресса к цели за `dtS` секунд. */
export function stepProgress(p: number, target: number, dtS: number): number {
  if (p === target) {
    return p;
  }
  if (target > p) {
    return Math.min(target, p + dtS / FROST_BURST_S);
  }
  return Math.max(target, p - dtS / FROST_THAW_S);
}

/**
 * Круг оттаивания (ТЗ §118). `strength` от 0 до 1 — насколько лёд под кругом
 * растаял; `leaving` — курсор круг покинул, и он зарастает. `originX/Y` — место
 * рождения: от него отсчитывается шаг, в пределах которого круг следует за курсором.
 */
export interface FrostHole {
  x: number;
  y: number;
  strength: number;
  leaving: boolean;
  originX: number;
  originY: number;
}

/** Убирает лишние круги: сначала самые слабые из уходящих. */
function trimHoles(holes: FrostHole[]): FrostHole[] {
  const result = holes;
  while (result.length > FROST_HOLE_MAX) {
    let weakest = -1;
    for (let k = 0; k < result.length; k++) {
      const hole = result[k];
      if (
        hole?.leaving === true &&
        (weakest < 0 || hole.strength < (result[weakest]?.strength ?? 0))
      ) {
        weakest = k;
      }
    }
    // Уходящих нет, остался один живой: предел не нарушается.
    if (weakest < 0) {
      break;
    }
    result.splice(weakest, 1);
  }
  return result;
}

/**
 * Реакция на курсор. Живой круг один: в пределах шага от места рождения он
 * следует за курсором, дальше — уходит, а на месте курсора рождается новый с
 * силой 0. `null` (курсор покинул область) делает живой круг уходящим.
 */
export function pointHoles(
  holes: readonly FrostHole[],
  point: { x: number; y: number } | null,
  radius: number,
): FrostHole[] {
  const next = holes.map((hole) => ({ ...hole }));
  const liveIndex = next.findIndex((hole) => !hole.leaving);
  const live = liveIndex < 0 ? undefined : next[liveIndex];
  if (point === null) {
    if (live !== undefined) {
      live.leaving = true;
    }
    return next;
  }
  if (live !== undefined) {
    if (Math.hypot(point.x - live.originX, point.y - live.originY) <= FROST_HOLE_STEP * radius) {
      live.x = point.x;
      live.y = point.y;
      return next;
    }
    live.leaving = true;
  }
  next.push({
    x: point.x,
    y: point.y,
    strength: 0,
    leaving: false,
    originX: point.x,
    originY: point.y,
  });
  return trimHoles(next);
}

/** Шаг времени: живые круги набирают силу, уходящие теряют и при нуле удаляются. */
export function stepHoles(holes: readonly FrostHole[], dtS: number): FrostHole[] {
  const next: FrostHole[] = [];
  for (const hole of holes) {
    if (hole.leaving) {
      const strength = hole.strength - dtS / FROST_HOLE_OUT_S;
      if (strength > 0) {
        next.push({ ...hole, strength });
      }
    } else {
      next.push({ ...hole, strength: Math.min(1, hole.strength + dtS / FROST_HOLE_IN_S) });
    }
  }
  return next;
}

/** Движется ли хоть один круг: уходящий или ещё не набравший силу. */
export function holesAnimating(holes: readonly FrostHole[]): boolean {
  return holes.some((hole) => hole.leaving || hole.strength < 1);
}

/**
 * Градиент маски круга: в центре непрозрачность `1 − strength`, до 70 % радиуса
 * держится, от радиуса — 1 (лёд на месте). Цвет в маске не важен, важна только
 * альфа, поэтому цвета темы тут нет.
 */
export function holeGradient(hole: FrostHole, radius: number): string {
  const inner = Math.round((1 - FROST_HOLE_FEATHER) * 100);
  return `radial-gradient(circle ${radius}px at ${hole.x}px ${hole.y}px, rgb(0 0 0 / ${1 - hole.strength}) ${inner}%, rgb(0 0 0 / 1) 100%)`;
}

/** Круг оттаивания рождает только мышь: касание и перо — нет (§118 п. 3). */
export function isThawPointer(pointerType: string): boolean {
  return pointerType === "mouse";
}

/** Точка курсора в системе оверлея; не мышь (касание, перо) круга не рождает. */
export function thawPoint(
  event: { pointerType: string; clientX: number; clientY: number },
  rect: { left: number; top: number },
): { x: number; y: number } | null {
  if (!isThawPointer(event.pointerType)) {
    return null;
  }
  return { x: event.clientX - rect.left, y: event.clientY - rect.top };
}
